import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import type { PoiItem } from '@/types/map';
import type { RoutePoint } from '@/types/route-simulation';
import type { IBuilding } from 'dabeeomaps/dist/src/model/map/IBuilding';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { findBuildingByMapId, toBuildingMapId } from './poi';
import { isValidNumber } from './number';

export type StoreFloorWithBuilding = FloorInfo & {
    buildingId: string;
};

export const isStoreFloorWithBuilding = (floor: IDataFloor | StoreFloorWithBuilding): floor is StoreFloorWithBuilding => {
    return 'buildingId' in floor && 'floorCode' in floor;
};

export const getStoreFloorCode = (floor: FloorInfo): string => {
    const legacy = floor as FloorInfo & { floor_code?: string };
    return legacy.floor_code ?? floor.floorCode ?? '';
};

export const getStoreFloorKoName = (floor: FloorInfo): string => floor.name?.ko ?? '';

export const isMultiBuildingStore = (buildings?: BuildingInStoreInfo[]): boolean => {
    return Array.isArray(buildings) && buildings.length >= 2;
};

export const buildAllStoreFloors = (buildings?: BuildingInStoreInfo[]): StoreFloorWithBuilding[] => {
    if (!Array.isArray(buildings) || !buildings.length) {
        return [];
    }

    return buildings.flatMap((building) =>
        (building.floors ?? []).map((floor) => ({
            ...floor,
            buildingId: toBuildingMapId(building.studioBuildingId, building.storeCode),
        }))
    );
};

export const getStoreFloorsByBuildingMapId = (buildings?: BuildingInStoreInfo[], buildingMapId?: string): StoreFloorWithBuilding[] => {
    const building = findBuildingByMapId(buildings, buildingMapId);
    if (!building?.floors?.length) {
        return [];
    }

    const buildingId = toBuildingMapId(building.studioBuildingId, building.storeCode);
    return building.floors.map((floor) => ({
        ...floor,
        buildingId,
    }));
};

export const collectCategoryPoisForFloorKoName = (
    floorKoName: string,
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[]
): PoiItem[] => {
    return categoriesCode.flatMap((categoryCode) => {
        const poi = groupedPoiByFloorId.get(floorKoName)?.get(categoryCode);
        return poi ?? [];
    });
};

const resolveStoreFloorForPois = (
    pois: PoiItem[],
    preferredFloors: StoreFloorWithBuilding[],
    allStoreFloors: StoreFloorWithBuilding[]
): StoreFloorWithBuilding | null => {
    if (!pois.length) {
        return null;
    }

    const poiFloorIds = new Set(pois.map((poi) => poi.floorId).filter((id): id is string => Boolean(id)));

    const fromPreferred = preferredFloors.find((floor) => poiFloorIds.has(getStoreFloorCode(floor)));
    if (fromPreferred) {
        return fromPreferred;
    }

    return allStoreFloors.find((floor) => poiFloorIds.has(getStoreFloorCode(floor))) ?? null;
};

const resolveFloorWithCategoryPoi = (
    floorKoName: string,
    preferredFloors: StoreFloorWithBuilding[],
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: StoreFloorWithBuilding[]
): StoreFloorWithBuilding | null => {
    const pois = collectCategoryPoisForFloorKoName(floorKoName, groupedPoiByFloorId, categoriesCode);
    if (!pois.length) {
        return null;
    }

    const storeFloors = allStoreFloors.length ? allStoreFloors : preferredFloors;
    return resolveStoreFloorForPois(pois, preferredFloors, storeFloors);
};

export const filterCategoryPoisByStoreFloors = (pois: PoiItem[], storeFloors: StoreFloorWithBuilding[]): PoiItem[] => {
    if (!Array.isArray(pois) || !pois.length) {
        return [];
    }

    const floorCodes = new Set(storeFloors.map((floor) => getStoreFloorCode(floor)).filter(Boolean));
    if (!floorCodes.size) {
        return pois;
    }

    return pois.filter((poi) => poi.floorId && floorCodes.has(poi.floorId));
};

const listStoreFloorsWithCategoryPoi = (
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: StoreFloorWithBuilding[]
): StoreFloorWithBuilding[] => {
    const results: StoreFloorWithBuilding[] = [];
    const seen = new Set<string>();

    for (const floor of allStoreFloors) {
        const floorKoName = getStoreFloorKoName(floor);
        const pois = collectCategoryPoisForFloorKoName(floorKoName, groupedPoiByFloorId, categoriesCode);
        const resolved = resolveStoreFloorForPois(pois, [floor], allStoreFloors);
        if (!resolved) {
            continue;
        }

        if (resolved.buildingId !== floor.buildingId || getStoreFloorCode(resolved) !== getStoreFloorCode(floor)) {
            continue;
        }

        const key = `${resolved.buildingId}_${getStoreFloorCode(resolved)}`;
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        results.push(resolved);
    }

    return results;
};

const getCategoryFloorDistanceScore = (
    currentFloorKoName: string,
    kioskBuildingFloors: StoreFloorWithBuilding[],
    candidate: StoreFloorWithBuilding,
    allStoreFloors: StoreFloorWithBuilding[]
): number => {
    const kioskBuildingId = kioskBuildingFloors[0]?.buildingId;
    const currentIdx = kioskBuildingFloors.findIndex((floor) => getStoreFloorKoName(floor) === currentFloorKoName);
    const candidateBuildingFloors = allStoreFloors.filter((floor) => floor.buildingId === candidate.buildingId);
    const candidateIdx = candidateBuildingFloors.findIndex((floor) => getStoreFloorCode(floor) === getStoreFloorCode(candidate));
    const indexDistance = Math.abs(candidateIdx - (currentIdx >= 0 ? currentIdx : 0));
    const crossBuildingPenalty = candidate.buildingId !== kioskBuildingId ? 10_000 : 0;

    return crossBuildingPenalty + indexDistance;
};

/** Nearest store floor (any building) that has category POI on the kiosk building's current floor list context. */
export const findNearestCategoryStoreFloor = (
    currentFloorKoName: string,
    kioskBuildingFloors: StoreFloorWithBuilding[],
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: StoreFloorWithBuilding[] = []
): StoreFloorWithBuilding | null => {
    const nearestInKioskBuildingOrder = findNearestStoreFloorWithPoi(
        kioskBuildingFloors,
        currentFloorKoName,
        groupedPoiByFloorId,
        categoriesCode,
        allStoreFloors
    );

    if (nearestInKioskBuildingOrder) {
        return nearestInKioskBuildingOrder;
    }

    const candidates = listStoreFloorsWithCategoryPoi(groupedPoiByFloorId, categoriesCode, allStoreFloors);
    if (!candidates.length) {
        return null;
    }

    let bestFloor: StoreFloorWithBuilding | null = null;
    let bestScore = Infinity;

    for (const candidate of candidates) {
        const score = getCategoryFloorDistanceScore(currentFloorKoName, kioskBuildingFloors, candidate, allStoreFloors);
        if (score < bestScore) {
            bestScore = score;
            bestFloor = candidate;
        }
    }

    return bestFloor;
};

const qrHereUrl = import.meta.env.VITE_QR_HERE_SERVICE_URL;

export const filterAndSortFloors = (floors: IDataFloor[], sortOrder: 'asc' | 'desc') => {
    if (!Array.isArray(floors)) return [];

    const filteredFloors = floors.filter((floor) => floor?.id);

    return filteredFloors.sort((a, b) => (sortOrder === 'asc' ? a.order - b.order : b.order - a.order));
};

export const getMapBuildingIdByFloorId = (mapBuildings?: IBuilding[], floorId?: string): string | undefined => {
    if (!floorId || !Array.isArray(mapBuildings) || !mapBuildings.length) {
        return undefined;
    }

    for (const building of mapBuildings) {
        if (building.floors?.some((floor) => floor.id === floorId)) {
            return building.id;
        }
    }

    return undefined;
};

/** Map SDK floors — single-building / Jamsil (legacy category flow). */
export const findNearestFloorWithPoi = (
    sortedFloors: IDataFloor[],
    name: string,
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[]
): IDataFloor | null => {
    if (!Array.isArray(sortedFloors)) {
        return null;
    }

    const floorIdx = sortedFloors.findIndex((floor) => floor?.name[0].text === name);

    if (floorIdx === -1) {
        return null;
    }

    const hasPoi = (floorName: string) => {
        const poiList = categoriesCode.flatMap((item) => {
            const poi = groupedPoiByFloorId.get(floorName)?.get(item);
            return poi ? poi : [];
        });
        return poiList && poiList.length > 0;
    };

    if (hasPoi(name)) {
        return sortedFloors.find((floor) => floor?.name[0].text === name) ?? null;
    }

    let left = floorIdx - 1;
    let right = floorIdx + 1;

    while (left >= 0 || right < sortedFloors.length) {
        if (left >= 0 && hasPoi(sortedFloors[left]?.name[0]?.text)) {
            return sortedFloors[left];
        }
        if (right < sortedFloors.length && hasPoi(sortedFloors[right]?.name[0]?.text)) {
            return sortedFloors[right];
        }
        left--;
        right++;
    }

    return null;
};

/** Store config floors — multi-building category flow (2+ buildings, non-Jamsil). */
export const findNearestStoreFloorWithPoi = (
    sortedFloors: StoreFloorWithBuilding[],
    name: string,
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: StoreFloorWithBuilding[] = []
): StoreFloorWithBuilding | null => {
    if (!Array.isArray(sortedFloors)) {
        return null;
    }

    const floorIdx = sortedFloors.findIndex((floor) => getStoreFloorKoName(floor) === name);

    if (floorIdx === -1) {
        return null;
    }

    const currentFloor = sortedFloors[floorIdx];
    const currentResolved = resolveFloorWithCategoryPoi(
        name,
        currentFloor ? [currentFloor] : [],
        groupedPoiByFloorId,
        categoriesCode,
        allStoreFloors
    );
    if (currentResolved) {
        return currentResolved;
    }

    let left = floorIdx - 1;
    let right = floorIdx + 1;

    while (left >= 0 || right < sortedFloors.length) {
        if (left >= 0) {
            const neighborFloor = sortedFloors[left];
            const neighborResolved = resolveFloorWithCategoryPoi(
                getStoreFloorKoName(neighborFloor),
                neighborFloor ? [neighborFloor] : [],
                groupedPoiByFloorId,
                categoriesCode,
                allStoreFloors
            );
            if (neighborResolved) {
                return neighborResolved;
            }
        }
        if (right < sortedFloors.length) {
            const neighborFloor = sortedFloors[right];
            const neighborResolved = resolveFloorWithCategoryPoi(
                getStoreFloorKoName(neighborFloor),
                neighborFloor ? [neighborFloor] : [],
                groupedPoiByFloorId,
                categoriesCode,
                allStoreFloors
            );
            if (neighborResolved) {
                return neighborResolved;
            }
        }
        left--;
        right++;
    }

    return null;
};

export const generateQrHereUrl = (serviceId: string, startPoint: RoutePoint, PoiIdEndPoint: string) => {
    if (!qrHereUrl || !startPoint?.position?.x || !startPoint?.position?.y || !startPoint?.floorId || !PoiIdEndPoint) {
        return '';
    }
    return `${qrHereUrl}/${serviceId}?${startPoint?.position?.x},${startPoint?.position?.y},FL-${startPoint.floorId},PO-${PoiIdEndPoint}`;
};

export const getFloorItemById = (floors?: IDataFloor[], floorId?: string) => {
    if (!Array.isArray(floors) || !floors?.length || !floorId) {
        return undefined;
    }
    return floors.find((floor) => floor?.id === floorId);
};

export const getAllFloorsFromIndoorBuildings = (buildings?: IBuilding[]): IDataFloor[] => {
    if (!Array.isArray(buildings) || !buildings?.length) {
        return [];
    }

    const indoorBuildings = buildings.filter((building) => building.buildingType === 'indoor');

    if (!indoorBuildings.length) {
        return [];
    }

    const allFloors: IDataFloor[] = [];
    indoorBuildings.forEach((building) => {
        building.floors.forEach((floor) => {
            allFloors.push(floor);
        });
    });

    return allFloors;
};

export const getUniqueFloorsByKoreanName = (floors: IDataFloor[]): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    const floorMap = new Map<string, number>();
    const uniqueFloors: IDataFloor[] = [];

    floors.forEach((floor) => {
        const koName = floor.name.find((n) => n.lang === 'ko');
        if (koName && !floorMap.has(koName.text)) {
            floorMap.set(koName.text, floor.order);
            uniqueFloors.push(floor);
        }
    });

    return uniqueFloors;
};

export const extractUniqueFloorsFromBuildings = (buildings?: IBuilding[]): IDataFloor[] => {
    const allFloors = getAllFloorsFromIndoorBuildings(buildings);
    return getUniqueFloorsByKoreanName(allFloors);
};

export const sortFloorsByOrder = (floors: IDataFloor[], direction: 'asc' | 'desc'): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    return [...floors].sort((a, b) => (direction === 'asc' ? a.order - b.order : b.order - a.order));
};

export const degreeToRadian = (degree?: number) => {
    return isValidNumber(degree) ? degree * (Math.PI / 180) : 0;
};

export const getDirectionVectors = (theta: number) => {
    const angle = -theta;

    const cosT = Math.cos(angle);
    const sinT = Math.sin(angle);

    const up = { x: sinT, y: -cosT };

    const right = { x: cosT, y: sinT };

    return {
        up,
        down: { x: -up.x, y: -up.y },
        left: { x: -right.x, y: -right.y },
        right,
    };
};

export const getPoiObjectIds = (poiList?: PoiItem[]): string[] => {
    if (!Array.isArray(poiList) || !poiList?.length) {
        return [];
    }

    const objectIds = poiList.map((item) => item?.objectId);
    return objectIds.filter((item) => item !== undefined);
};

/**
 * Helper function: Đợi map render xong trước khi thực hiện moveTo
 * Sử dụng requestAnimationFrame để đảm bảo map đã vẽ xong frame hiện tại
 * @param frames - Số frames cần đợi (mặc định là 3 frames để đảm bảo)
 */
export const waitForMapRender = async (frames: number = 3): Promise<void> => {
    return new Promise<void>((resolve) => {
        let frameCount = 0;

        const waitFrame = () => {
            frameCount++;
            if (frameCount >= frames) {
                resolve();
            } else {
                requestAnimationFrame(waitFrame);
            }
        };

        requestAnimationFrame(waitFrame);
    });
};

/**
 * Helper function với fallback timeout: Đợi map render xong hoặc timeout
 * Kết hợp RAF với fallback timeout để đảm bảo không bị block vô hạn
 * @param frames - Số frames cần đợi (mặc định 3)
 * @param maxTimeout - Timeout tối đa (ms, mặc định 200ms)
 */
export const waitForMapRenderWithTimeout = async (frames: number = 3, maxTimeout: number = 200): Promise<void> => {
    return Promise.race([
        // Phương án 1: Đợi frames với RAF
        waitForMapRender(frames),
        // Phương án 2: Timeout fallback
        new Promise<void>((resolve) => setTimeout(resolve, maxTimeout)),
    ]);
};

/**
 * Helper function nâng cao: Đợi và verify marker đã được vẽ trên map
 * Sử dụng hybrid approach: RAF + polling + timeout để đảm bảo marker thực sự được render
 * @param currentMap - DabeeoMap instance
 * @param markerId - ID của marker cần verify
 * @param position - Vị trí marker {x, y} để verify
 * @param maxRetries - Số lần retry tối đa (mặc định 10)
 * @param debugContext - Context để debug log
 */
export const waitForMarkerReady = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    markerId?: string,
    _position?: { x?: number; y?: number },
    maxRetries: number = 10,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap || !markerId) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid params for waitForMarkerReady`);
        return false;
    }

    // Tier 1: Đợi 3 frames trước (đảm bảo browser đã paint)
    await waitForMapRender(3);

    // Tier 2: Polling để verify marker thực sự tồn tại
    let retryCount = 0;
    const retryDelays = [50, 50, 100, 100, 150, 200, 250, 300]; // Progressive backoff

    while (retryCount < maxRetries) {
        try {
            // Kiểm tra marker có tồn tại không (nếu map API hỗ trợ)
            // Một số map engine có method getMarker hoặc markers.get
            const markerExists = currentMap?.markers?.get?.(markerId);

            if (markerExists) {
                if (debugContext) {
                    // eslint-disable-next-line no-console
                    console.log(`✅ [${debugContext}] Marker ready after ${retryCount} retries`);
                }
                return true;
            }

            // Nếu không có method get, chờ một thời gian hợp lý
            const delay = retryDelays[retryCount] || 300;
            await new Promise((resolve) => setTimeout(resolve, delay));

            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`[${debugContext}] Error checking marker:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] Marker not verified after ${maxRetries} retries, proceeding anyway`);
    }

    // Return true anyway để không block flow, nhưng log warning
    return true;
};

/**
 * Helper function: Safe moveTo với retry logic
 * Đảm bảo moveTo được thực thi thành công, retry nếu fail
 * @param currentMap - DabeeoMap instance
 * @param position - Position to move to
 * @param floorId - Floor ID
 * @param transition - Enable transition animation
 * @param maxRetries - Số lần retry tối đa (mặc định 3)
 * @param debugContext - Context để debug
 */
export const safeMoveToMarker = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    position: { x: number; y: number },
    floorId?: string,
    transition: boolean = true,
    maxRetries: number = 3,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap?.control?.moveTo) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Map control not available`);
        return false;
    }

    if (!isValidNumber(position?.x) || !isValidNumber(position?.y)) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid position:`, position);
        return false;
    }

    let retryCount = 0;

    while (retryCount < maxRetries) {
        try {
            if (debugContext && retryCount > 0) {
                // eslint-disable-next-line no-console
                console.log(`[${debugContext}] Retry moveTo attempt ${retryCount + 1}/${maxRetries}`);
            }

            currentMap.control.moveTo({
                position: { x: position.x, y: position.y },
                floorId,
                transition,
            });

            // Đợi một frame để verify
            await waitForMapRender(1);

            // Get current camera position để verify moveTo đã work
            const currentCamera = currentMap?.control?.getCurrentCameraInfo?.();
            if (currentCamera?.position) {
                const distance = Math.sqrt(Math.pow(currentCamera.position.x - position.x, 2) + Math.pow(currentCamera.position.y - position.y, 2));

                // Nếu khoảng cách nhỏ hơn threshold, coi như thành công
                const threshold = 1000; // Điều chỉnh tùy map scale
                if (distance < threshold || retryCount === maxRetries - 1) {
                    if (debugContext) {
                        // eslint-disable-next-line no-console
                        console.log(`✅ [${debugContext}] MoveTo successful (distance: ${distance.toFixed(2)})`);
                    }
                    return true;
                }
            }

            // Nếu chưa thành công, đợi thêm
            await new Promise((resolve) => setTimeout(resolve, 100));
            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`[${debugContext}] MoveTo error:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] MoveTo completed after ${maxRetries} attempts`);
    }

    return true;
};
